import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Copy, Check, Send, Search, ExternalLink } from 'lucide-react';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SharePostModal = ({ open, onClose, post }) => {
  const [copied, setCopied] = useState(false);
  const [friends, setFriends] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState({});
  const [sent, setSent] = useState({});
  const shareUrl = `${window.location.origin}/post/${post?.id}`;

  useEffect(() => {
    if (open) {
      fetchFriends();
      setSent({});
    }
  }, [open]);

  const fetchFriends = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      const res = await axios.get(`${API}/friends/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFriends(res.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const sendToDevotee = async (friend) => {
    setSending(prev => ({ ...prev, [friend.id]: true }));
    try {
      const token = localStorage.getItem('dharmbook_token');
      // Get or create conversation
      const convRes = await axios.get(`${API}/conversations/${friend.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Send the post as a message
      const message = `Shared a post: "${post?.content?.slice(0, 80) || 'Check this out'}${post?.content?.length > 80 ? '...' : ''}"`;
      await axios.post(`${API}/messages`, {
        conversation_id: convRes.data.id,
        content: message
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSent(prev => ({ ...prev, [friend.id]: true }));
      toast.success(`Shared with ${friend.name}`);
    } catch {
      toast.error('Failed to share');
    } finally {
      setSending(prev => ({ ...prev, [friend.id]: false }));
    }
  };

  const filtered = friends.filter(f =>
    f.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="rounded-2xl max-w-md p-0 overflow-hidden">
        <DialogHeader className="p-4 pb-0">
          <DialogTitle className="text-lg font-semibold">Share Post</DialogTitle>
        </DialogHeader>

        {/* Send to Devotees */}
        <div className="px-4 pt-3">
          <p className="text-sm font-medium text-gray-700 mb-2">Send to Devotees</p>
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search devotees..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-gray-50 rounded-lg text-sm border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FF9933]/40 focus:border-[#FF9933]"
              data-testid="share-search-input"
            />
          </div>

          <div className="max-h-48 overflow-y-auto space-y-1 -mx-1">
            {loading ? (
              <div className="flex justify-center py-6">
                <div className="w-5 h-5 border-2 border-[#FF9933] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filtered.length === 0 ? (
              <p className="text-center text-sm text-gray-400 py-4">
                {friends.length === 0 ? 'No devotees yet' : 'No match found'}
              </p>
            ) : (
              filtered.map(friend => (
                <div
                  key={friend.id}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors mx-1"
                  data-testid={`share-devotee-${friend.id}`}
                >
                  <img
                    src={friend.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(friend.name)}&background=FF9933&color=fff`}
                    alt={friend.name}
                    className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                  />
                  <span className="flex-1 text-sm font-medium text-gray-900 truncate">{friend.name}</span>
                  <button
                    onClick={() => sendToDevotee(friend)}
                    disabled={sending[friend.id] || sent[friend.id]}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1 ${
                      sent[friend.id]
                        ? 'bg-green-50 text-green-600'
                        : 'bg-[#FF9933] hover:bg-[#E68A00] text-white'
                    }`}
                    data-testid={`send-to-${friend.id}`}
                  >
                    {sent[friend.id] ? (
                      <><Check className="w-3.5 h-3.5" /> Sent</>
                    ) : sending[friend.id] ? (
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <><Send className="w-3.5 h-3.5" /> Send</>
                    )}
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-100 mx-4 mt-3" />

        {/* Copy & External */}
        <div className="p-4 pt-3 space-y-2">
          <button
            onClick={copyLink}
            className="w-full flex items-center gap-3 px-4 py-2.5 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm font-medium text-gray-800 transition-colors"
            data-testid="share-copy-link"
          >
            {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-500" />}
            {copied ? 'Copied!' : 'Copy Link'}
          </button>
          <button
            onClick={() => {
              window.open(`https://wa.me/?text=${encodeURIComponent(shareUrl)}`, '_blank');
            }}
            className="w-full flex items-center gap-3 px-4 py-2.5 bg-green-50 hover:bg-green-100 rounded-lg text-sm font-medium text-green-700 transition-colors"
            data-testid="share-whatsapp"
          >
            <ExternalLink className="w-4 h-4" />
            Share via WhatsApp
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SharePostModal;
